'use client';

import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import gsap from 'gsap';
import Image from 'next/image';
import type { IconType } from 'react-icons';
import { FiGithub, FiLinkedin, FiTwitter, FiMail } from 'react-icons/fi';
import { useContactModal } from '../ContactModal/ContactModalContext';
import { useScrollLock } from '../ContactModal/ScrollLockContext';
import { useIsMobile } from '../ContactModal/useIsMobile';
import { Button, SocialLink } from '@/shared/ui';
import { navbarMock } from '@/shared/store/mocks';
import styles from './navbar.module.scss';

const NAV_SOCIAL_ICONS: Record<(typeof navbarMock.socials)[number]['id'], IconType> = {
    github: FiGithub,
    linkedin: FiLinkedin,
    twitter: FiTwitter,
    email: FiMail,
};

const SCROLL_ACTIVE_OFFSET = 120;

export default function Navbar() {
    const { navItems, socials, logoSrc, logoText, ctaLabel } = navbarMock;
    const [activeSection, setActiveSection] = useState('home');
    const [menuOpen, setMenuOpen] = useState(false);
    const navRef = useRef<HTMLElement>(null);
    const indicatorRef = useRef<HTMLDivElement>(null);
    const navItemsRef = useRef<HTMLDivElement>(null);
    const isFirstRun = useRef(true);
    const scrollingToSectionRef = useRef(false);
    const pendingScrollIdRef = useRef<string | null>(null);
    const isMobile = useIsMobile();

    const scrollToSection = (id: string) => {
        scrollingToSectionRef.current = true;
        setActiveSection(id);
        if (isMobile && menuOpen) {
            pendingScrollIdRef.current = id;
            setMenuOpen(false);
        } else {
            const element = document.getElementById(id);
            element?.scrollIntoView({ behavior: 'smooth' });
            setMenuOpen(false);
        }
        setTimeout(() => {
            scrollingToSectionRef.current = false;
        }, 2000);
    };

    const { openModal: openContactModal } = useContactModal();
    const closeMenu = () => setMenuOpen(false);
    const toggleMenu = () => {
        if (menuOpen) {
            setMenuOpen(false);
        } else {
            // Даємо браузеру відмалювати closed-стан (translateX(100%)), щоб на реальному девайсі спрацювала transition
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setMenuOpen(true));
            });
        }
    };

    useEffect(() => {
        const el = navRef.current;
        if (!el) return;
        gsap.fromTo(el, { y: -100, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' });
    }, []);

    const { lock, unlock } = useScrollLock();
    useEffect(() => {
        if (menuOpen) lock();
        else unlock();
        return () => unlock();
    }, [menuOpen, lock, unlock]);

    // Після закриття меню на мобільному — скролимо до секції (після unlock, коли body вже не fixed)
    useEffect(() => {
        if (menuOpen || !pendingScrollIdRef.current) return;
        const id = pendingScrollIdRef.current;
        pendingScrollIdRef.current = null;
        const raf = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const element = document.getElementById(id);
                element?.scrollIntoView({ behavior: 'smooth' });
            });
        });
        return () => cancelAnimationFrame(raf);
    }, [menuOpen]);

    useEffect(() => {
        const updateActiveFromScroll = () => {
            if (scrollingToSectionRef.current) return;
            let current: string = 'home';
            for (let i = navItems.length - 1; i >= 0; i--) {
                const el = document.getElementById(navItems[i].id);
                if (!el) continue;
                const top = el.getBoundingClientRect().top;
                if (top <= SCROLL_ACTIVE_OFFSET) {
                    current = navItems[i].id;
                    break;
                }
            }
            setActiveSection(prev => (prev === current ? prev : current));
        };

        updateActiveFromScroll();
        window.addEventListener('scroll', updateActiveFromScroll, { passive: true });
        return () => window.removeEventListener('scroll', updateActiveFromScroll);
    }, [navItems]);

    useEffect(() => {
        const indicator = indicatorRef.current;
        const container = navItemsRef.current;
        if (!indicator || !container) return;
        const activeBtn = container.querySelector(`[data-section="${activeSection}"]`);
        if (!activeBtn) return;
        const containerRect = container.getBoundingClientRect();
        const btnRect = activeBtn.getBoundingClientRect();
        const vars = {
            left: btnRect.left - containerRect.left,
            top: btnRect.top - containerRect.top,
            width: btnRect.width,
            height: btnRect.height,
        };
        if (isFirstRun.current) {
            gsap.set(indicator, vars);
            isFirstRun.current = false;
        } else {
            gsap.killTweensOf(indicator);
            gsap.fromTo(
                indicator,
                { scaleX: 1.08, scaleY: 0.95 },
                {
                    ...vars,
                    scaleX: 1,
                    scaleY: 1,
                    duration: 0.4,
                    ease: 'power2.out',
                    overwrite: true,
                },
            );
        }
    }, [activeSection]);

    return (
        <nav ref={navRef} className={styles.nav}>
            <div className={styles.wrapper}>
                <div className={styles.inner}>
                    <div className={styles.glow} aria-hidden />
                    <div className={styles.flex}>
                        <Button
                            type="button"
                            className={styles.logoBlock}
                            onClick={() => scrollToSection('home')}
                            aria-label="Home"
                            startIcon={
                                <div className={styles.logoIcon}>
                                    <Image src={logoSrc} alt="" width={40} height={40} />
                                </div>
                            }
                            classNames={{ text: styles.logoText }}
                        >
                            {logoText}
                        </Button>

                        <div ref={navItemsRef} className={styles.navItems}>
                            <div ref={indicatorRef} className={styles.indicator} aria-hidden />
                            {navItems.map(item => (
                                <Button
                                    key={item.id}
                                    type="button"
                                    data-section={item.id}
                                    onClick={() => scrollToSection(item.id)}
                                    className={clsx(styles.navBtn, activeSection === item.id && styles.navBtnActive)}
                                >
                                    <span>{item.label}</span>
                                </Button>
                            ))}
                        </div>

                        <Button
                            type="button"
                            className={clsx(styles.menuBtn, menuOpen && styles.menuBtnOpen)}
                            onClick={toggleMenu}
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={menuOpen}
                        >
                            <span className={styles.burger}>
                                <span className={styles.burgerLine} />
                                <span className={styles.burgerLine} />
                                <span className={styles.burgerLine} />
                            </span>
                        </Button>
                    </div>
                </div>
            </div>

            <div
                className={clsx(styles.mobileOverlay, menuOpen && styles.mobileOverlayOpen)}
                onClick={closeMenu}
                aria-hidden
            />
            <div
                className={clsx(styles.mobileMenu, menuOpen && styles.mobileMenuOpen)}
                role="dialog"
                aria-modal="true"
                aria-label="Menu"
            >
                <header className={styles.mobileMenuHeader}>
                    <Button
                        type="button"
                        className={styles.mobileMenuLogo}
                        onClick={() => scrollToSection('home')}
                        aria-label="Home"
                        startIcon={
                            <div className={styles.mobileMenuLogoIcon}>
                                <Image src={logoSrc} alt="" width={40} height={40} />
                            </div>
                        }
                        classNames={{ text: styles.mobileMenuLogoText }}
                    >
                        {logoText}
                    </Button>
                    <Button
                        type="button"
                        className={styles.mobileMenuClose}
                        onClick={closeMenu}
                        aria-label="Close menu"
                    >
                        <span className={styles.mobileMenuCloseIcon} aria-hidden />
                    </Button>
                </header>
                <div className={styles.mobileMenuInner}>
                    {navItems.map(item => (
                        <Button
                            key={item.id}
                            type="button"
                            onClick={() => scrollToSection(item.id)}
                            className={clsx(styles.mobileNavBtn, activeSection === item.id && styles.mobileNavBtnActive)}
                        >
                            {item.label}
                        </Button>
                    ))}
                </div>
                <div className={styles.mobileMenuFooter}>
                    <div className={styles.mobileMenuSocials}>
                        {socials.map(s => (
                            <SocialLink
                                key={s.id}
                                href={s.href}
                                icon={NAV_SOCIAL_ICONS[s.id]}
                                label={s.label}
                                size={22}
                                className={styles.mobileMenuSocialLink}
                            />
                        ))}
                    </div>
                    <Button
                        type="button"
                        className={styles.mobileMenuCta}
                        onClick={() => {
                            openContactModal();
                            closeMenu();
                        }}
                    >
                        {ctaLabel}
                    </Button>
                </div>
            </div>
        </nav>
    );
}
